import React, { useState } from 'react';

export type MonthlySearch = {
  year: number;
  month: number;
  search_volume: number | null;
};

export type SearchVolumeData = {
  keyword: string;
  location_code: number;
  language_code: string;
  search_partners: boolean;
  search_volume: number | null;
  competition: string | null;
  competition_index: number | null;
  cpc: number | null;
  low_top_of_page_bid: number | null;
  high_top_of_page_bid: number | null;
  monthly_searches: MonthlySearch[];
  latest_monthly_search_volume: number | null;
  raw: unknown;
};

type Props = {
  data: SearchVolumeData | null;
  cached: boolean;
  loading: boolean;
  error: string | null;
  onFetch: () => void;
  onForceFetch?: () => void;
};

const formatNumber = (value: number | null | undefined) =>
  typeof value === 'number' ? value.toLocaleString('ja-JP') : '-';

const formatCurrency = (value: number | null | undefined) =>
  typeof value === 'number' ? `$${value.toFixed(2)}` : '-';

const SearchVolumeCard: React.FC<Props> = ({ data, cached, loading, error, onFetch, onForceFetch }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const monthly = data?.monthly_searches || [];
  const width = 360;
  const height = 150;
  const padding = { top: 14, right: 12, bottom: 24, left: 34 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  const maxValue = Math.max(1, ...monthly.map((item) => item.search_volume || 0));
  const barWidth = monthly.length > 0 ? chartWidth / monthly.length : 0;
  const hoveredItem = hoveredIndex === null ? null : monthly[hoveredIndex];

  return (
    <section className="bg-white shadow-sm rounded-lg p-6 min-w-0">
      <div className="flex items-start justify-between border-b pb-2 mb-4 gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-bold text-gray-800">検索ボリューム</h2>
            {cached && (
              <span className="rounded bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700">
                保存済みデータを表示中
              </span>
            )}
          </div>
          <p className="mt-1 text-[11px] text-gray-500">DataForSEO / Google Ads / 日本</p>
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-2">
          {data && onForceFetch && (
            <button
              type="button"
              onClick={onForceFetch}
              disabled={loading}
              className="rounded border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              再取得
            </button>
          )}
          <button
            type="button"
            onClick={() => onFetch()}
            disabled={loading}
            className="bg-blue-500 text-white px-3 py-2 rounded text-sm font-semibold hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? '取得中...' : 'ボリュームを取得'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {error}
        </div>
      )}

      {!data ? (
        <div className="h-[260px] flex items-center justify-center text-center text-sm text-gray-500">
          ボタンを押すと検索ボリュームを取得します
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-700 break-words">{data.keyword}</p>
          </div>

          <div className="grid grid-cols-2 gap-2 text-center">
            <div className="rounded border border-gray-100 bg-slate-50 p-2">
              <p className="text-[11px] text-gray-500">月間平均</p>
              <p className="text-lg font-bold text-gray-800">{formatNumber(data.search_volume)}</p>
            </div>
            <div className="rounded border border-gray-100 bg-slate-50 p-2">
              <p className="text-[11px] text-gray-500">直近月</p>
              <p className="text-lg font-bold text-gray-800">{formatNumber(data.latest_monthly_search_volume)}</p>
            </div>
            <div className="rounded border border-gray-100 bg-slate-50 p-2">
              <p className="text-[11px] text-gray-500">CPC</p>
              <p className="text-lg font-bold text-gray-800">{formatCurrency(data.cpc)}</p>
            </div>
            <div className="rounded border border-gray-100 bg-slate-50 p-2">
              <p className="text-[11px] text-gray-500">競合</p>
              <p className="text-lg font-bold text-gray-800">{data.competition || '-'}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
            <div className="rounded border border-gray-100 p-2">
              <span className="text-gray-500">競合指数</span>
              <span className="float-right font-semibold">{formatNumber(data.competition_index)}</span>
            </div>
            <div className="rounded border border-gray-100 p-2">
              <span className="text-gray-500">低入札</span>
              <span className="float-right font-semibold">{formatCurrency(data.low_top_of_page_bid)}</span>
            </div>
            <div className="rounded border border-gray-100 p-2">
              <span className="text-gray-500">高入札</span>
              <span className="float-right font-semibold">{formatCurrency(data.high_top_of_page_bid)}</span>
            </div>
            <div className="rounded border border-gray-100 p-2">
              <span className="text-gray-500">検索パートナー</span>
              <span className="float-right font-semibold">{data.search_partners ? '含む' : '含まない'}</span>
            </div>
          </div>

          {monthly.length > 0 && (
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-[170px]" role="img" aria-label="monthly search volume chart">
              {[0, 0.5, 1].map((rate) => {
                const y = padding.top + chartHeight - rate * chartHeight;
                return (
                  <line key={rate} x1={padding.left} y1={y} x2={width - padding.right} y2={y} stroke="#e5e7eb" strokeWidth="1" />
                );
              })}
              <text x={padding.left - 8} y={padding.top + 4} textAnchor="end" fontSize="10" fill="#94a3b8">
                {formatNumber(maxValue)}
              </text>
              {monthly.map((item, index) => {
                const value = item.search_volume || 0;
                const barHeight = (value / maxValue) * chartHeight;
                const x = padding.left + index * barWidth + 2;
                const y = padding.top + chartHeight - barHeight;
                const visibleBarWidth = Math.max(2, barWidth - 4);
                return (
                  <g key={`${item.year}-${item.month}`}>
                    <rect
                      x={x}
                      y={y}
                      width={visibleBarWidth}
                      height={Math.max(1, barHeight)}
                      rx="2"
                      fill={hoveredIndex === index ? '#1d4ed8' : '#2563eb'}
                      opacity={hoveredIndex === null || hoveredIndex === index ? 0.88 : 0.45}
                    />
                    <rect
                      x={padding.left + index * barWidth}
                      y={padding.top}
                      width={barWidth}
                      height={chartHeight}
                      fill="transparent"
                      onMouseEnter={() => setHoveredIndex(index)}
                      onMouseLeave={() => setHoveredIndex(null)}
                    >
                      <title>{`${item.year}/${item.month}: ${formatNumber(item.search_volume)}`}</title>
                    </rect>
                  </g>
                );
              })}
              {hoveredItem && hoveredIndex !== null && (
                <g pointerEvents="none">
                  {(() => {
                    const value = hoveredItem.search_volume || 0;
                    const barHeight = (value / maxValue) * chartHeight;
                    const barCenterX = padding.left + hoveredIndex * barWidth + barWidth / 2;
                    const barTopY = padding.top + chartHeight - barHeight;
                    const tooltipWidth = 96;
                    const tooltipHeight = 34;
                    const tooltipX = Math.min(
                      width - padding.right - tooltipWidth,
                      Math.max(padding.left, barCenterX - tooltipWidth / 2)
                    );
                    const tooltipY = Math.max(4, barTopY - tooltipHeight - 8);

                    return (
                      <>
                        <line
                          x1={barCenterX}
                          y1={padding.top}
                          x2={barCenterX}
                          y2={padding.top + chartHeight}
                          stroke="#1d4ed8"
                          strokeWidth="1"
                          strokeDasharray="3 3"
                          opacity="0.5"
                        />
                        <rect
                          x={tooltipX}
                          y={tooltipY}
                          width={tooltipWidth}
                          height={tooltipHeight}
                          rx="6"
                          fill="#0f172a"
                          opacity="0.94"
                        />
                        <text x={tooltipX + 8} y={tooltipY + 14} fontSize="10" fill="#cbd5e1">
                          {hoveredItem.year}/{hoveredItem.month}
                        </text>
                        <text x={tooltipX + 8} y={tooltipY + 28} fontSize="12" fontWeight="700" fill="#ffffff">
                          {formatNumber(hoveredItem.search_volume)}
                        </text>
                      </>
                    );
                  })()}
                </g>
              )}
              <text x={padding.left} y={height - 8} fontSize="10" fill="#64748b">
                {monthly[0]?.year}/{monthly[0]?.month}
              </text>
              <text x={width - padding.right} y={height - 8} textAnchor="end" fontSize="10" fill="#64748b">
                {monthly[monthly.length - 1]?.year}/{monthly[monthly.length - 1]?.month}
              </text>
            </svg>
          )}
        </div>
      )}
    </section>
  );
};

export default SearchVolumeCard;
